import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import Cookie from 'js-cookie';
import { fetchOrganizationProfile, fetchProfiles } from 'api/portal';

export const STATUS_IDLE = 'idle';
export const STATUS_LOADING = 'loading';
export const STATUS_SUCCEEDED = 'succeeded';
export const STATUS_FAILED = 'failed';

const getBrowserLanguage = () => {
  const browserLanguage = navigator.language.toLocaleLowerCase();

  if (browserLanguage.startsWith('pt')) {
    return 'pt-br';
  }

  return 'en-us';
};

const initialState = {
  // User data returned from the API
  profile: {
    birth_date: '',
    doctor_document: '',
    document: '',
    first_name: '',
    gender: '',
    id: -1,
    last_name: '',
    phone_number: '',
    photo: '',
    notification_language: '',
    braincare_permissions: [],
  },
  // Organization data from the API
  active_organization: {
    id: -1,
    name: '',
    photo: '',
    password_policy: {},
  },
  // Member data from the API
  organization_profile: {
    id: -1,
    email: '',
    phone_number: '',
    enable_2fa: false,
    two_factor_exempt_until: null,
    roles: [],
    units: [],
    status: '',
    member_permissions: [],
  },
  organizations: [],
  isBraincareUser: Cookie.get('isBraincareUser') === 'true',
  language: Cookie.get('language') ? Cookie.get('language') : getBrowserLanguage(),
  status: STATUS_IDLE,
};

export const updateProfile = createAsyncThunk('me/fetchProfile', async () => {
  const token = Cookie.get('access_token');

  if (token) {
    const response = await fetchOrganizationProfile();

    const { data } = response;

    const profile = data.user;
    const activeOrganization = data.organization;
    const memberId = data.id;
    const memberEmail = data.email;
    const memberPhoneNumber = data.phone_number;
    const memberEnable2FA = data.enable_2fa;
    const memberTwoFactorExemptUntil = data.two_factor_exempt_until ?? null;
    const memberRoles = data.roles;
    const memberUnits = data.units;
    const memberStatus = data.status;
    const memberPermissions = data.member_permissions;

    return {
      profile,
      active_organization: activeOrganization,
      organization_profile: {
        id: memberId,
        email: memberEmail,
        phone_number: memberPhoneNumber,
        enable_2fa: memberEnable2FA,
        two_factor_exempt_until: memberTwoFactorExemptUntil,
        roles: memberRoles,
        units: memberUnits,
        status: memberStatus,
        member_permissions: memberPermissions,
      },
    };
  }

  return {};
});

export const updateOrganizations = createAsyncThunk('me/fetchOrganizations', async () => {
  const token = Cookie.get('access_token');

  if (token) {
    const response = await fetchProfiles();

    const { data } = response;

    return { organizations: data };
  }

  return {};
});

/**
 * Union by organization id: prefer order from the API, then append any orgs only present
 * in state (e.g. full list from direct-login before /me/organization refetch stabilizes).
 */
function mergeOrganizationsWithPrevious(incoming, previous) {
  if (!Array.isArray(incoming)) {
    return Array.isArray(previous) ? previous : [];
  }
  const prev = Array.isArray(previous) ? previous : [];
  const seen = new Set();
  const merged = [];
  incoming.forEach((o) => {
    if (o && o.id != null && !seen.has(o.id)) {
      seen.add(o.id);
      merged.push(o);
    }
  });
  prev.forEach((o) => {
    if (o && o.id != null && !seen.has(o.id)) {
      seen.add(o.id);
      merged.push(o);
    }
  });
  return merged;
}

const userSlice = createSlice({
  name: 'user',
  initialState,
  reducers: {
    clearUserData() {
      return initialState;
    },
    clearProfileData(state) {
      return {
        ...state,
        profile: initialState.profile,
      };
    },
    clearActiveOrganizationData(state) {
      return {
        ...state,
        active_organization: initialState.active_organization,
      };
    },
    clearOrganizationProfileData(state) {
      return {
        ...state,
        organization_profile: initialState.organization_profile,
      };
    },
    clearOrganizations(state) {
      return {
        ...state,
        organizations: initialState.organizations,
      };
    },
    setProfileData(state, action) {
      return {
        ...state,
        profile: {
          ...state.profile,
          ...action.payload,
        },
      };
    },
    setIsBraincareUser(state, action) {
      Cookie.set('isBraincareUser', action.payload, { sameSite: 'strict', expires: 365 });
      return {
        ...state,
        isBraincareUser: action.payload,
      };
    },
    setActiveOrganizationData(state, action) {
      return {
        ...state,
        active_organization: {
          ...state.active_organization,
          ...action.payload,
        },
      };
    },
    setCorporateProfileData(state, action) {
      const { payload } = action;

      const profile = payload.user;
      const activeOrganization = payload.organization;
      const memberId = payload.id;
      const memberEmail = payload.email;
      const memberPhoneNumber = payload.phone_number;
      const memberEnable2FA = payload.enable_2fa;
      const memberTwoFactorExemptUntil = payload.two_factor_exempt_until ?? null;
      const memberRoles = payload.roles;
      const memberUnits = payload.units;
      const memberStatus = payload.status;

      // Braincare admins must see isBraincareUser true before routes like /braincare/* mount
      // (see braincare Allowed). Login flows that only need org context call setIsBraincareUser(false) after this.
      const isBraincareUser = Boolean(profile?.braincare_permissions?.length);
      Cookie.set('isBraincareUser', isBraincareUser ? 'true' : 'false', { sameSite: 'strict', expires: 365 });

      return {
        ...state,
        profile,
        active_organization: activeOrganization,
        organization_profile: {
          id: memberId,
          email: memberEmail,
          phone_number: memberPhoneNumber,
          enable_2fa: memberEnable2FA,
          two_factor_exempt_until: memberTwoFactorExemptUntil,
          roles: memberRoles,
          units: memberUnits,
          status: memberStatus,
        },
        isBraincareUser,
      };
    },
    setOrganizationProfileData(state, action) {
      return {
        ...state,
        organization_profile: {
          ...state.organization_profile,
          ...action.payload,
        },
      };
    },
    setOrganizations(state, action) {
      return {
        ...state,
        organizations: action.payload,
      };
    },
    setLanguage(state, action) {
      Cookie.set('language', action.payload, { sameSite: 'strict', expires: 365 });
      return {
        ...state,
        language: action.payload,
      };
    },
  },
  extraReducers(builder) {
    builder
      .addCase(updateProfile.pending, (state) => ({
        ...state,
        status: STATUS_LOADING,
      }))
      .addCase(updateProfile.fulfilled, (state, action) => {
        const { isBraincareUser } = state;

        if (isBraincareUser) {
          return {
            ...state,
            profile: action.payload.profile,
            status: STATUS_SUCCEEDED,
          };
        }

        return {
          ...state,
          ...action.payload,
          status: STATUS_SUCCEEDED,
        };
      })
      .addCase(updateProfile.rejected, (state) => ({
        ...state,
        status: STATUS_FAILED,
      }))
      .addCase(updateOrganizations.fulfilled, (state, action) => {
        const { organizations: _ignore, ...rest } = action.payload || {};
        return {
          ...state,
          ...rest,
          organizations: mergeOrganizationsWithPrevious(
            action.payload?.organizations,
            state.organizations,
          ),
        };
      });
  },
});

export const {
  clearUserData,
  clearProfileData,
  clearActiveOrganizationData,
  clearOrganizationProfileData,
  clearOrganizations,
  setProfileData,
  setIsBraincareUser,
  setActiveOrganizationData,
  setCorporateProfileData,
  setOrganizationProfileData,
  setOrganizations,
  setLanguage,
} = userSlice.actions;

export default userSlice.reducer;
